import type { CheckDef, Outcome } from './types';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1';

export const REDIRECT_HOPS = 5;

/**
 * First-party fixtures. Third-party hosts made these checks skip on the device
 * ("network unreachable"), which grades nothing — everything a check needs now
 * comes off our own origin.
 */
export const IFRAME_PROBE = '/checklist/iframe-probe.html';
export const SAMPLE_MP4 = '/checklist/assets/sample.mp4';

/** wss:// twin of the worker's /checklist/ws-echo route. */
export function wsEchoUrl(): string {
  const url = new URL(`${API_BASE}/checklist/ws-echo`, window.location.href);
  url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';
  return url.toString();
}

/** Stable DOM ids for the media/iframe/input elements the rows render. */
export const ELEMENT_IDS = {
  camera: 'zw-el-camera',
  audioAutoplay: 'zw-el-audio-autoplay',
  videoMp4: 'zw-el-video-inline-mp4',
  videoHls: 'zw-el-hls-native',
  iframeSameOrigin: 'zw-el-iframe-same-origin',
  iframeCrossOrigin: 'zw-el-iframe-cross-origin',
  iframePostMessage: 'zw-el-postmessage-iframe',
  fileCapture: 'zw-el-file-input-capture',
  fileGeneric: 'zw-el-file-input-generic',
} as const;

const pass = (detail: string): Outcome => ({ status: 'pass', detail });
const fail = (detail: string): Outcome => ({ status: 'fail', detail });
const skip = (detail: string): Outcome => ({ status: 'skip', detail });
const err = (e: any) => String(e?.name || e?.message || e);

/** A transport-level failure must never red the suite (contract §8.3). */
const unreachable = (endpoint: string) => skip(`network unreachable: ${endpoint}`);

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Resolves once the element fires load, or false on error/timeout. */
function waitForLoad(el: HTMLIFrameElement, url: string, ms: number): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const settle = (ok: boolean) => {
      if (done) return;
      done = true;
      el.onload = null;
      el.onerror = null;
      resolve(ok);
    };
    el.onload = () => settle(true);
    el.onerror = () => settle(false);
    setTimeout(() => settle(false), ms);
    el.src = url;
  });
}


/**
 * Sends a nonce over `endpoint` and waits for it to come back. Resolves the
 * outcome when the socket answered at all, and `null` when the endpoint was
 * simply unreachable — that is the caller's cue to try the next one.
 */
function wsEcho(endpoint: string, timeoutMs: number): Promise<Outcome | null> {
  return new Promise((resolve) => {
    const nonce = `zw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(endpoint);
    } catch {
      return resolve(null);
    }
    let done = false;
    const settle = (o: Outcome | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try { ws.close(); } catch { /* already closing */ }
      resolve(o);
    };
    const timer = setTimeout(() => settle(null), timeoutMs);
    ws.onopen = () => ws.send(nonce);
    ws.onmessage = (e) => {
      const data = String(e.data);
      // Some public echo servers greet before echoing; ignore anything else.
      if (data.includes(nonce)) settle(pass(`echo from ${endpoint}: ${data.slice(0, 40)}`));
    };
    ws.onerror = () => settle(null);
    ws.onclose = () => settle(null);
  });
}

/** Fires a scheme hand-off. Recording happens before the OS takes over. */
function handOff(url: string, target: string): Outcome {
  try {
    window.location.href = url;
    return pass(`invoked: ${target}`);
  } catch (e) {
    return fail(`${target} threw: ${err(e)}`);
  }
}

/**
 * Every check the page knows about, in the order the rows are rendered.
 * The `id` column is the contract — see types.ts.
 */
export const CHECKS: CheckDef[] = [
  {
    id: 'camera',
    title: 'Camera',
    desc: 'getUserMedia({ video: true }) — webview must surface camera permission prompt.',
    bucket: 'permission',
    description: 'Opens a video capture stream; headless once CAMERA is granted.',
    async run(ctx) {
      if (!navigator.mediaDevices?.getUserMedia) return skip('no mediaDevices.getUserMedia');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        const v = ctx.el<HTMLVideoElement>(ELEMENT_IDS.camera);
        if (v) {
          v.srcObject = stream;
          v.play().catch(() => {});
        }
        const tracks = stream.getVideoTracks().length;
        setTimeout(() => stream.getTracks().forEach((t) => t.stop()), 4000);
        return pass(`tracks=${tracks}`);
      } catch (e: any) {
        const name = String(e?.name || '');
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          return fail(`CAMERA permission denied: ${err(e)}`);
        }
        if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          // No video input matched — typical for an emulator with no camera
          // or a WebView that does not enumerate devices. Not a permission issue.
          let cams = -1;
          try {
            const devs = await navigator.mediaDevices.enumerateDevices();
            cams = devs.filter((d) => d.kind === 'videoinput').length;
          } catch {}
          return skip(`no camera device (${name}, videoinput=${cams})`);
        }
        return fail(`getUserMedia: ${err(e)}`);
      }
    },
  },
  {
    id: 'geolocation',
    title: 'Geolocation',
    desc: 'navigator.geolocation — needed for geo-gated casino content.',
    bucket: 'permission',
    description: 'Reads a position fix; needs app-side onGeolocationPermissionsShowPrompt.',
    run(ctx) {
      if (!('geolocation' in navigator)) return Promise.resolve(skip('no geolocation API'));
      return new Promise<Outcome>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve(pass(`lat=${pos.coords.latitude.toFixed(3)} lon=${pos.coords.longitude.toFixed(3)}`)),
          (e) => {
            // No app-side prompt handler at all looks exactly like a silent
            // denial; report it as a permission failure with the code.
            resolve(fail(`ACCESS_FINE_LOCATION permission: ${e.code} ${e.message}`));
          },
          { timeout: Math.min(ctx.timeoutMs, 10000) },
        );
      });
    },
  },
  {
    id: 'file-input-capture',
    title: 'Photo / camera capture (file input)',
    desc: '<input type="file" accept="image/*" capture> — system picker / camera roll.',
    bucket: 'external',
    description: 'Opens the camera/gallery picker; completion needs an external driver.',
    async run(ctx) {
      const input = ctx.el<HTMLInputElement>(ELEMENT_IDS.fileCapture);
      if (!input) return skip('file input not mounted');
      try {
        input.click();
        return pass('invoked: camera/gallery picker');
      } catch (e) {
        return fail(`picker threw: ${err(e)}`);
      }
    },
  },
  {
    id: 'file-input-generic',
    title: 'Any file upload',
    desc: '<input type="file"> — generic document picker.',
    bucket: 'external',
    description: 'Opens the generic document picker; completion needs an external driver.',
    async run(ctx) {
      const input = ctx.el<HTMLInputElement>(ELEMENT_IDS.fileGeneric);
      if (!input) return skip('file input not mounted');
      try {
        input.click();
        return pass('invoked: document picker');
      } catch (e) {
        return fail(`picker threw: ${err(e)}`);
      }
    },
  },
  {
    id: 'cookies-same-site',
    title: 'Cookies (same-site)',
    desc: 'document.cookie set+read.',
    bucket: 'auto',
    description: 'Writes and reads back a first-party cookie, then deletes it.',
    async run() {
      try {
        const v = `zw-${Date.now()}`;
        document.cookie = `zw_test=${v}; path=/; max-age=60; SameSite=Lax`;
        const got = document.cookie.split('; ').find((c) => c.startsWith('zw_test='))?.split('=')[1];
        document.cookie = 'zw_test=; path=/; max-age=0; SameSite=Lax';
        return got === v ? pass(`set+read ok (${v})`) : fail(`wrote ${v} got ${got ?? 'nothing'}`);
      } catch (e) {
        return fail(err(e));
      }
    },
  },
  {
    id: 'cookies-cross-origin',
    title: 'Cross-origin cookies (fetch credentials: include)',
    desc: 'Verify webview accepts 3rd-party Set-Cookie. Uses httpbin.org.',
    bucket: 'auto',
    description: 'Cross-origin credentialed fetch against httpbin.org.',
    async run() {
      try {
        const res = await fetch('https://httpbin.org/cookies/set?zw3p=ok', {
          credentials: 'include',
          mode: 'cors',
        });
        return res.ok
          ? pass(`fetched cross-origin, status=${res.status}`)
          : fail(`cross-origin fetch status=${res.status}`);
      } catch {
        return unreachable('https://httpbin.org');
      }
    },
  },
  {
    id: 'web-storage',
    title: 'localStorage + sessionStorage',
    bucket: 'auto',
    description: 'Roundtrips a key through localStorage and sessionStorage, then removes it.',
    async run() {
      const detail: string[] = [];
      let ok = true;
      try {
        localStorage.setItem('zw_probe', '1');
        if (localStorage.getItem('zw_probe') === '1') detail.push('localStorage✓');
        else { detail.push('localStorage✗'); ok = false; }
        localStorage.removeItem('zw_probe');
      } catch { detail.push('localStorage✗'); ok = false; }
      try {
        sessionStorage.setItem('zw_probe', '1');
        if (sessionStorage.getItem('zw_probe') === '1') detail.push('sessionStorage✓');
        else { detail.push('sessionStorage✗'); ok = false; }
        sessionStorage.removeItem('zw_probe');
      } catch { detail.push('sessionStorage✗'); ok = false; }
      return ok ? pass(detail.join(' ')) : fail(detail.join(' '));
    },
  },
  {
    id: 'indexeddb',
    title: 'IndexedDB',
    bucket: 'auto',
    description: 'Writes and reads one record, then deletes the database.',
    run() {
      if (!('indexedDB' in window)) return Promise.resolve(skip('no IndexedDB'));
      return new Promise<Outcome>((resolve) => {
        const finish = (o: Outcome, db?: IDBDatabase) => {
          try { db?.close(); } catch { /* already closed */ }
          indexedDB.deleteDatabase('zw-test');
          resolve(o);
        };
        try {
          const req = indexedDB.open('zw-test', 1);
          req.onupgradeneeded = () => { req.result.createObjectStore('s'); };
          req.onerror = () => resolve(fail(String(req.error?.message || req.error)));
          req.onsuccess = () => {
            const db = req.result;
            const tx = db.transaction('s', 'readwrite');
            tx.objectStore('s').put('v', 'k');
            tx.onerror = () => finish(fail(String(tx.error?.message || tx.error)), db);
            tx.oncomplete = () => {
              const g = db.transaction('s', 'readonly').objectStore('s').get('k');
              g.onsuccess = () =>
                finish(g.result === 'v' ? pass('roundtrip ok') : fail(`got=${g.result}`), db);
              g.onerror = () => finish(fail(String(g.error?.message || g.error)), db);
            };
          };
        } catch (e) {
          resolve(fail(err(e)));
        }
      });
    },
  },
  {
    id: 'iframe-same-origin',
    title: 'Iframe (same-origin)',
    desc: 'Loads a same-origin page inside an iframe.',
    bucket: 'auto',
    description: 'Frames a same-origin page; needs X-Frame-Options: SAMEORIGIN or frame-ancestors self.',
    // The frame points at a static probe, not at this SPA: framing the app
    // itself made the check race React's mount and read an empty body, which
    // says nothing about whether the WebView renders a child frame.
    async run(ctx) {
      const el = ctx.el<HTMLIFrameElement>(ELEMENT_IDS.iframeSameOrigin);
      if (!el) return skip('iframe not mounted');
      const ok = await waitForLoad(el, IFRAME_PROBE, ctx.timeoutMs - 500);
      if (!ok) return fail('same-origin frame did not load (X-Frame-Options / CSP?)');
      try {
        const body = (el.contentDocument?.body?.textContent ?? '').trim();
        return body.includes('zw-iframe-ok')
          ? pass(`same-origin iframe rendered: ${body.slice(0, 40)}`)
          : fail(`loaded but body was ${JSON.stringify(body.slice(0, 40))}`);
      } catch (e) {
        return fail(`loaded but not same-origin readable: ${err(e)}`);
      }
    },
  },
  {
    id: 'iframe-cross-origin',
    title: 'Iframe (cross-origin)',
    desc: 'Loads example.com inside an iframe — verifies webview allows 3rd-party frames.',
    bucket: 'auto',
    description: 'Frames example.com; a transport failure resolves as skip.',
    async run(ctx) {
      const el = ctx.el<HTMLIFrameElement>(ELEMENT_IDS.iframeCrossOrigin);
      if (!el) return skip('iframe not mounted');
      const ok = await waitForLoad(el, 'https://example.com/', ctx.timeoutMs - 500);
      // A cross-origin frame gives us nothing but the load event, and a blocked
      // frame is indistinguishable from an unreachable host from in here.
      return ok ? pass('loaded — visually confirm content') : unreachable('https://example.com');
    },
  },
  {
    id: 'clipboard',
    title: 'Clipboard read',
    bucket: 'manual',
    description: 'navigator.clipboard.readText(); needs a real gesture.',
    async run() {
      try {
        if (!navigator.clipboard?.readText) return fail('Clipboard API not exposed by this WebView');
        const got = await navigator.clipboard.readText();
        return pass(got ? `read ${got.length} chars` : 'read ok (clipboard empty)');
      } catch (e) {
        return fail(err(e));
      }
    },
  },
  {
    id: 'audio-autoplay',
    title: 'Audio autoplay (with sound, after gesture)',
    desc: 'HTMLAudioElement.play() — many webviews require a user gesture, this button counts.',
    bucket: 'auto',
    description: 'Plays audible audio; skips when the platform still demands a gesture.',
    async run(ctx) {
      const a = ctx.el<HTMLAudioElement>(ELEMENT_IDS.audioAutoplay);
      if (!a) return skip('audio element not mounted');
      try {
        await a.play();
        setTimeout(() => a.pause(), 1000);
        return pass('audio playing');
      } catch (e: any) {
        if (String(e?.name) === 'NotAllowedError') return skip('autoplay needs user activation');
        return fail(err(e));
      }
    },
  },
  {
    id: 'deeplink-tg',
    title: 'Deep link (tg://)',
    desc: 'Navigates to a custom scheme — verify OS hands off to the target app.',
    bucket: 'external',
    description: 'Navigates to tg:// so the OS hands off to Telegram.',
    async run() {
      return handOff('tg://resolve?domain=telegram', 'tg://');
    },
  },
  {
    id: 'webgl',
    title: 'WebGL',
    desc: 'Casino slot UIs often rely on hardware-accelerated rendering.',
    bucket: 'auto',
    description: 'Acquires a WebGL context and reads the renderer string.',
    async run() {
      try {
        const c = document.createElement('canvas');
        const gl = (c.getContext('webgl') || c.getContext('experimental-webgl')) as WebGLRenderingContext | null;
        if (!gl) return fail('no WebGL context');
        const dbg = gl.getExtension('WEBGL_debug_renderer_info');
        const renderer = dbg ? gl.getParameter((dbg as any).UNMASKED_RENDERER_WEBGL) : 'renderer masked';
        return pass(String(renderer));
      } catch (e) {
        return fail(err(e));
      }
    },
  },
  {
    id: 'websocket',
    title: 'WebSocket',
    desc: "wss:// echo — verifies persistent connections aren't blocked.",
    bucket: 'auto',
    description: 'Echoes a nonce off our own wss endpoint, falling back to a public one.',
    // First-party first: the public echo service was unreachable from the test
    // device, which skipped the check without ever exercising a socket.
    async run(ctx) {
      const budget = Math.min(ctx.timeoutMs - 500, 12000);
      const first = wsEchoUrl();
      const ours = await wsEcho(first, Math.min(budget / 2, 5000));
      if (ours) return ours;
      const fallback = 'wss://echo.websocket.events';
      const theirs = await wsEcho(fallback, Math.min(budget / 2, 5000));
      if (theirs) return theirs;
      return unreachable(`${first} and ${fallback}`);
    },
  },
  {
    id: 'video-inline-mp4',
    title: 'Inline MP4 video',
    desc: 'HTMLVideoElement with playsInline — webview must render video without going fullscreen.',
    bucket: 'auto',
    description: 'Plays an MP4 inline and asserts currentTime advances.',
    async run(ctx) {
      const v = ctx.el<HTMLVideoElement>(ELEMENT_IDS.videoMp4);
      if (!v) return skip('video element not mounted');
      try {
        await v.play();
        const from = v.currentTime;
        await sleep(1200);
        const to = v.currentTime;
        v.pause();
        return to > from
          ? pass(`playing inline, currentTime ${from.toFixed(2)}→${to.toFixed(2)}`)
          : fail(`play() resolved but currentTime stuck at ${to.toFixed(2)}`);
      } catch (e: any) {
        if (String(e?.name) === 'NotSupportedError') return unreachable(SAMPLE_MP4);
        return fail(err(e));
      }
    },
  },
  {
    id: 'hls-native',
    title: 'HLS (.m3u8) native',
    desc: 'Tests built-in HLS support (iOS/Safari). Without it, app needs hls.js.',
    bucket: 'auto',
    description: 'Checks for native HLS playback; Android WebView has none by design.',
    async run(ctx) {
      const v = ctx.el<HTMLVideoElement>(ELEMENT_IDS.videoHls);
      if (!v) return skip('video element not mounted');
      const canNative = v.canPlayType('application/vnd.apple.mpegurl');
      if (!canNative) return skip('no native HLS in Android WebView; app uses hls.js');
      try {
        await v.play();
        setTimeout(() => v.pause(), 3000);
        return pass(`native HLS (canPlayType=${canNative})`);
      } catch (e) {
        return fail(err(e));
      }
    },
  },
  {
    id: 'redirect-fetch',
    title: `Redirect chain — fetch (${REDIRECT_HOPS}× 302)`,
    desc: 'XHR/fetch must follow a multi-hop 302 chain to the end. Fails if the webview or a proxy caps redirects.',
    bucket: 'auto',
    description: `Follows a ${REDIRECT_HOPS}-hop 302 chain with fetch().`,
    async run() {
      const started = performance.now();
      let res: Response;
      try {
        res = await fetch(`${API_BASE}/checklist/redirect/${REDIRECT_HOPS}?ts=${Date.now()}`);
      } catch {
        return unreachable(`${API_BASE}/checklist/redirect`);
      }
      const json: any = await res.json().catch(() => null);
      const hops = json?.data?.hops;
      const ms = Math.round(performance.now() - started);
      if (res.ok && hops === REDIRECT_HOPS) {
        return pass(`${REDIRECT_HOPS} hops followed in ${ms}ms · redirected=${res.redirected} · final=${res.url}`);
      }
      return fail(`status=${res.status} hops=${hops ?? 'n/a'} redirected=${res.redirected} final=${res.url}`);
    },
  },
  {
    id: 'redirect-navigation',
    title: `Redirect chain — navigation (${REDIRECT_HOPS} hops)`,
    desc: 'Leaves this page through a chain and comes back. Pick the hop type: 302s, meta refresh, JS location, or all three mixed — the shape real offer links use. The page reloads; the result appears here on return.',
    bucket: 'navigates',
    description: `Top-level navigation through a ${REDIRECT_HOPS}-hop chain and back.`,
    async run(ctx) {
      const mode = 'mixed';
      // The landing URL is built by the worker, so runner mode has to be asked
      // for on the way in or the page comes back with the harness switched off.
      ctx.leave(
        `${API_BASE}/checklist/redirect/${REDIRECT_HOPS}?mode=${mode}&dest=page&ts=${Date.now()}`
          + (ctx.runner ? '&runner=1' : ''),
        { mode, hops: String(REDIRECT_HOPS) },
      );
      // The page is unloading; the verdict is recorded by resume() on return.
      return new Promise<Outcome>(() => {});
    },
    resume({ query, params, elapsedMs }) {
      if (query.get('redirect') !== 'done') return fail('did not return');
      const hops = query.get('hops') ?? params.hops;
      const mode = query.get('mode') ?? params.mode;
      return pass(`${hops} hops (mode=${mode}) survived a top-level navigation in ${elapsedMs}ms`);
    },
  },
  {
    id: 'cleartext-http',
    title: 'Cleartext HTTP (top-level)',
    desc: 'Leaves this page to http:// on this same host and comes back through the 301. Fails outright when the app is built without usesCleartextTraffic — the case that breaks http offer links. Subresource probes cannot test this: the browser upgrades or blocks them before they leave.',
    bucket: 'navigates',
    // Cleartext cannot be probed as a subresource: from an HTTPS page the
    // browser upgrades or blocks an http:// fetch/image before it leaves the
    // device. A top-level navigation to http:// on our own host is the real
    // thing, and it is exactly what an offer link does.
    description: 'Top-level http:// navigation on this host, returning through the 301.',
    async run(ctx) {
      ctx.leave(
        `http://${window.location.host}/checklist/webview-test?cleartext=done&ts=${Date.now()}`
          + (ctx.runner ? '&runner=1&autorun=0' : ''),
        {},
      );
      return new Promise<Outcome>(() => {});
    },
    resume({ query, elapsedMs }) {
      if (query.get('cleartext') !== 'done') return fail('did not return');
      return pass(`cleartext navigation went through and redirected back in ${elapsedMs}ms`);
    },
  },
  {
    id: 'webassembly',
    title: 'WebAssembly',
    desc: 'Instantiate a tiny module and call add(2, 40).',
    bucket: 'auto',
    description: 'Instantiates a tiny module and calls add(2, 40).',
    async run() {
      try {
        // Tiny module exporting add(i32, i32) -> i32.
        const bytes = new Uint8Array([
          0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00,
          0x01, 0x07, 0x01, 0x60, 0x02, 0x7f, 0x7f, 0x01, 0x7f,
          0x03, 0x02, 0x01, 0x00,
          0x07, 0x07, 0x01, 0x03, 0x61, 0x64, 0x64, 0x00, 0x00,
          0x0a, 0x09, 0x01, 0x07, 0x00, 0x20, 0x00, 0x20, 0x01, 0x6a, 0x0b,
        ]);
        const mod = await WebAssembly.instantiate(bytes);
        const add = (mod.instance.exports as any).add as (a: number, b: number) => number;
        const got = add(2, 40);
        return got === 42 ? pass('add(2,40)=42') : fail(`add(2,40)=${got}`);
      } catch (e) {
        return fail(err(e));
      }
    },
  },
  {
    id: 'postmessage-iframe',
    title: 'postMessage iframe',
    desc: 'Sends a token to a same-origin iframe and waits for the echo back.',
    bucket: 'auto',
    description: 'Sends a token to a same-origin iframe and waits for the echo.',
    async run(ctx) {
      const iframe = ctx.el<HTMLIFrameElement>(ELEMENT_IDS.iframePostMessage);
      if (!iframe) return skip('iframe not mounted');
      const token = `zw-${Date.now()}`;
      const loaded = await waitForLoad(iframe, '/checklist/webview-test?pm=1', ctx.timeoutMs - 1500);
      if (!loaded) return fail('echo iframe did not load');
      return new Promise<Outcome>((resolve) => {
        const settle = (o: Outcome) => {
          window.removeEventListener('message', onMsg);
          clearTimeout(timer);
          resolve(o);
        };
        const onMsg = (e: MessageEvent) => {
          if (e.data && (e.data as any).zw === token) settle(pass(`iframe replied with token ${token}`));
        };
        window.addEventListener('message', onMsg);
        const timer = setTimeout(() => settle(fail('no reply from the iframe')), Math.min(ctx.timeoutMs - 1000, 4000));
        iframe.contentWindow?.postMessage({ zw: token }, window.location.origin);
      });
    },
  },
  {
    id: 'intent-scheme',
    title: 'intent:// (Android)',
    desc: 'Custom Android intent scheme — verify the OS handles it.',
    bucket: 'external',
    description: 'Navigates to an intent:// URL so Android resolves it.',
    async run() {
      return handOff('intent://example.com#Intent;scheme=https;end', 'intent://');
    },
  },

];

export const CHECK_BY_ID: Record<string, CheckDef> = Object.fromEntries(
  CHECKS.map((c) => [c.id, c]),
);
